import type { Drawing, PenDrawing, TextDrawing, ShapeDrawing, Point } from "./types";

function drawPen(ctx: CanvasRenderingContext2D, drawing: PenDrawing) {
    if (drawing.points.length < 2) return;

    ctx.save();
    ctx.strokeStyle = drawing.color;
    ctx.lineWidth = drawing.lineWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.beginPath();
    ctx.moveTo(drawing.points[0].x, drawing.points[0].y);
    for (let i = 1; i < drawing.points.length; i++) {
        ctx.lineTo(drawing.points[i].x, drawing.points[i].y);
    }
    ctx.stroke();
    ctx.restore();
}

/**
 * Word-wrap text into lines that fit within maxWidth.
 */
function wrapText(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
): string[] {
    const lines: string[] = [];
    // Split by explicit newlines first
    const paragraphs = text.split("\n");

    for (const paragraph of paragraphs) {
        if (paragraph === "") {
            lines.push("");
            continue;
        }
        const words = paragraph.split(" ");
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const testLine = currentLine + " " + words[i];
            const metrics = ctx.measureText(testLine);
            if (metrics.width > maxWidth && currentLine.length > 0) {
                lines.push(currentLine);
                currentLine = words[i];
            } else {
                currentLine = testLine;
            }
        }
        lines.push(currentLine);
    }

    return lines;
}

const TEXT_PADDING_X_RATIO = 0.4;
const TEXT_PADDING_Y_RATIO = 0.25;
const TEXT_LINE_HEIGHT_RATIO = 1.4;

function setTextFont(ctx: CanvasRenderingContext2D, drawing: TextDrawing) {
    ctx.font = `bold ${drawing.fontSize}px system-ui, -apple-system, sans-serif`;
}

/**
 * Layout box of a text drawing, in the same space its `position` is stored in.
 *
 * `position` is the box's top-left and `maxWidth` its full width — the visible
 * white background is centered within that width, so the layout box's center
 * and the visible center coincide. Only the height has to be derived, from the
 * wrapped line count.
 *
 * Shared with {@link drawText} so the measurement and the render can't drift,
 * and used by rotation to re-anchor text about its center.
 */
export function measureTextDrawing(
    ctx: CanvasRenderingContext2D,
    drawing: TextDrawing,
): { width: number; height: number } {
    ctx.save();
    setTextFont(ctx, drawing);
    const padding = drawing.fontSize * TEXT_PADDING_X_RATIO;
    const lines = wrapText(ctx, drawing.text, drawing.maxWidth - padding * 2);
    ctx.restore();
    return {
        width: drawing.maxWidth,
        height:
            lines.length * drawing.fontSize * TEXT_LINE_HEIGHT_RATIO +
            drawing.fontSize * TEXT_PADDING_Y_RATIO * 2,
    };
}

function drawText(ctx: CanvasRenderingContext2D, drawing: TextDrawing) {
    if (!drawing.text) return;

    ctx.save();
    setTextFont(ctx, drawing);
    ctx.textBaseline = "top";
    ctx.textAlign = "center";

    const padding = drawing.fontSize * TEXT_PADDING_X_RATIO;
    const paddingY = drawing.fontSize * TEXT_PADDING_Y_RATIO;
    const lineHeight = drawing.fontSize * TEXT_LINE_HEIGHT_RATIO;
    const borderRadius = drawing.fontSize * 0.6;

    // Wrap text within maxWidth (minus padding)
    const contentMaxWidth = drawing.maxWidth - padding * 2;
    const lines = wrapText(ctx, drawing.text, contentMaxWidth);

    // Measure actual text width (widest line)
    let textWidth = 0;
    for (const line of lines) {
        const w = ctx.measureText(line).width;
        if (w > textWidth) textWidth = w;
    }

    const bgWidth = Math.min(textWidth + padding * 2, drawing.maxWidth);
    const bgHeight = lines.length * lineHeight + paddingY * 2;

    // Draw white rounded rectangle background, centered within maxWidth
    const bgX = drawing.position.x + (drawing.maxWidth - bgWidth) / 2;
    const bgY = drawing.position.y;
    drawRoundedRect(ctx, bgX, bgY, bgWidth, bgHeight, borderRadius);
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.fill();

    // Draw text lines centered
    ctx.fillStyle = drawing.color;
    // Reset shadow — no text shadow needed with white bg
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;

    const textX = bgX + bgWidth / 2;
    let textY = bgY + paddingY;

    for (const line of lines) {
        ctx.fillText(line, textX, textY);
        textY += lineHeight;
    }

    ctx.restore();
}

function drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
) {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

function drawRectangleShape(ctx: CanvasRenderingContext2D, drawing: ShapeDrawing) {
    const x = Math.min(drawing.start.x, drawing.end.x);
    const y = Math.min(drawing.start.y, drawing.end.y);
    const w = Math.abs(drawing.end.x - drawing.start.x);
    const h = Math.abs(drawing.end.y - drawing.start.y);
    ctx.strokeRect(x, y, w, h);
}

function drawCircleShape(ctx: CanvasRenderingContext2D, drawing: ShapeDrawing) {
    const cx = (drawing.start.x + drawing.end.x) / 2;
    const cy = (drawing.start.y + drawing.end.y) / 2;
    const rx = Math.abs(drawing.end.x - drawing.start.x) / 2;
    const ry = Math.abs(drawing.end.y - drawing.start.y) / 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
}

function drawArrowShape(ctx: CanvasRenderingContext2D, drawing: ShapeDrawing) {
    const { start, end } = drawing;
    // Main line
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    // Arrowhead
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const headLength = Math.max(20, drawing.lineWidth * 3);
    const headAngle = Math.PI / 6; // 30 degrees

    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(
        end.x - headLength * Math.cos(angle - headAngle),
        end.y - headLength * Math.sin(angle - headAngle)
    );
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(
        end.x - headLength * Math.cos(angle + headAngle),
        end.y - headLength * Math.sin(angle + headAngle)
    );
    ctx.stroke();
}

function drawShape(ctx: CanvasRenderingContext2D, drawing: ShapeDrawing) {
    ctx.save();
    ctx.strokeStyle = drawing.color;
    ctx.lineWidth = drawing.lineWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    switch (drawing.shapeType) {
        case "rectangle":
            drawRectangleShape(ctx, drawing);
            break;
        case "circle":
            drawCircleShape(ctx, drawing);
            break;
        case "arrow":
            drawArrowShape(ctx, drawing);
            break;
    }

    ctx.restore();
}

export function drawDrawing(ctx: CanvasRenderingContext2D, drawing: Drawing) {
    switch (drawing.type) {
        case "pen":
            drawPen(ctx, drawing);
            break;
        case "text":
            drawText(ctx, drawing);
            break;
        case "shape":
            drawShape(ctx, drawing);
            break;
    }
}

export function renderNonTextDrawings(
    ctx: CanvasRenderingContext2D,
    drawings: Drawing[],
    currentDrawing: Drawing | null
) {
    for (const drawing of drawings) {
        if (drawing.type !== "text") drawDrawing(ctx, drawing);
    }
    if (currentDrawing) drawDrawing(ctx, currentDrawing);
}

export function renderAllDrawings(
    ctx: CanvasRenderingContext2D,
    drawings: Drawing[],
    currentDrawing: Drawing | null
) {
    for (const drawing of drawings) {
        drawDrawing(ctx, drawing);
    }
    if (currentDrawing) {
        drawDrawing(ctx, currentDrawing);
    }
}

export function getPointFromEvent(
    e: React.MouseEvent | React.TouchEvent,
    canvas: HTMLCanvasElement
): Point {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX: number;
    let clientY: number;

    if ("touches" in e) {
        const touch = e.touches[0] || e.changedTouches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
    } else {
        clientX = e.clientX;
        clientY = e.clientY;
    }

    return {
        x: Math.max(0, Math.min((clientX - rect.left) * scaleX, canvas.width)),
        y: Math.max(0, Math.min((clientY - rect.top) * scaleY, canvas.height)),
    };
}

export function generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
